import { useEffect, useMemo } from 'react'
import { isRouteErrorResponse, Link, useLocation, useRouteError } from 'react-router'

import { reportClientError } from '@/api/errors'
import { DroppedCallArt } from '@/components/DroppedCallArt'
import { SURFACE_CLASS } from '@/components/Surface'
import { Button } from '@/components/ui/button'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { SUPPORT_EMAIL } from '@/lib/site'
import { cn } from '@/lib/utils'
import { NotFoundPage } from '@/routes/NotFoundPage'

/** Letters and digits that can't be misread when someone reads a reference out (no 0/O, 1/I/L). */
const REFERENCE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'

/** A short code for this crash ("ERR-7K2Q9C") that the person quotes and staff find in the logs. */
const _reference = (): string =>
  `ERR-${Array.from(crypto.getRandomValues(new Uint8Array(6)), (byte) => REFERENCE_ALPHABET[byte % REFERENCE_ALPHABET.length]).join('')}`

/** The error's message and stack, whatever was thrown. */
const _describe = (error: unknown): { message: string; stack?: string } => {
  if (error instanceof Error) return { message: error.message.slice(0, 500), stack: error.stack?.slice(0, 4000) }
  return { message: String(error).slice(0, 500) }
}

/**
 * What a person sees when a page breaks (the router's error boundary), instead of a stack trace: a picture of a
 * dropped call, that it's not their fault, a reference, and three ways on: try again, go home, or email support with
 * the reference, page and error already written in. The crash is reported to RowCall's logs with the same reference.
 * Inside an org it shows in the shell (the sidebar stays); elsewhere on its own. A missing page is Not found instead.
 */
export const ErrorPage = ({ isInShell = false }: { isInShell?: boolean }) => {
  useDocumentTitle('Something went wrong')
  const error = useRouteError()
  const { pathname } = useLocation()
  // One reference for this crash, kept across re-renders.
  const reference = useMemo(() => _reference(), [])
  const isNotFound = isRouteErrorResponse(error) && error.status === 404
  const { message, stack } = _describe(error)

  useEffect(() => {
    if (!isNotFound) void reportClientError({ reference, message, path: pathname, stack })
  }, [isNotFound, reference, message, pathname, stack])

  if (isNotFound) return <NotFoundPage />
  const mailto = `mailto:${SUPPORT_EMAIL}?${new URLSearchParams({
    subject: `Something broke in RowCall (${reference})`,
    body: `Hi RowCall team,\n\nSomething broke while I was using RowCall.\n\nReference: ${reference}\nPage: ${pathname}\nTime: ${new Date().toString()}\nError: ${message}\n\nWhat I was doing:\n`,
  })
    .toString()
    .replaceAll('+', '%20')}`

  const card = (
    <div
      data-slot="surface"
      className={cn(
        SURFACE_CLASS,
        'flex w-full max-w-lg flex-col items-center gap-6 text-center motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-safe:ease-fluid',
      )}
    >
      <DroppedCallArt />
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium tracking-tight">We dropped the call</h1>
        <p className="text-sm text-muted-foreground">
          Something on this page broke. It isn’t anything you did, and your flows, calls and results are safe. Try
          again, and if it keeps happening, email us the reference below so we can fix it.
        </p>
      </div>
      <p className="rounded-full bg-muted px-4 py-1.5 font-mono text-sm" aria-label="Reference">
        {reference}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button size="lg" onClick={() => window.location.reload()}>
          Try again
        </Button>
        <Button asChild size="lg" variant="secondary">
          <a href={mailto}>Email {SUPPORT_EMAIL}</a>
        </Button>
        <Button asChild size="lg" variant="ghost">
          <Link to="/" reloadDocument>
            Go to RowCall
          </Link>
        </Button>
      </div>
    </div>
  )
  if (isInShell) return <div className="flex justify-center py-10">{card}</div>
  return <main className="flex min-h-svh items-center justify-center bg-background px-4 py-6">{card}</main>
}
